import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { stockAdjustmentsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M1-INV-04 — Stock Adjustment detail view. Posted documents are immutable
 * (DB trigger backs this) so the screen is read-only once Status=Posted.
 *
 * Posting writes one InventoryLedger ADJUSTMENT row per line (positive delta) or
 * WRITE_OFF row (negative delta) — the row preview here mirrors what the handler does.
 */
export function StockAdjustmentDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canPost = useAuth(s => s.hasPermission("inventory.adjustment.post"));

  const [postOpen, setPostOpen] = useState(false);

  const query = useQuery({
    queryKey: ["stock-adjustment", id],
    queryFn: () => stockAdjustmentsApi.get(id),
    enabled: !!id,
  });

  const postMutation = useMutation({
    mutationFn: () => stockAdjustmentsApi.post(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-adjustment", id] });
      qc.invalidateQueries({ queryKey: ["stock-adjustments"] });
      setPostOpen(false);
    },
  });

  if (query.isLoading || !query.data) return <CircularProgress />;
  const sa = query.data;
  const isDraft = sa.status === "Draft";

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{sa.documentNo}</Typography>
          <Chip label={sa.status} color={STATUS_COLOR[sa.status] ?? "default"} size="small" />
        </Stack>
        {isDraft && canPost && (
          <Button variant="contained" color="success" startIcon={<PostAddIcon />}
            onClick={() => setPostOpen(true)}>
            Post adjustment
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">Header</Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 1 }}>
          <Stack>
            <Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography>{new Date(sa.documentDate).toLocaleDateString()}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Warehouse</Typography>
            <Typography>{sa.warehouseName}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Reason</Typography>
            <Typography>{sa.reason ?? "—"}</Typography>
          </Stack>
        </Stack>
        {sa.notes && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{sa.notes}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Ledger impact preview</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell align="right">Qty delta</TableCell>
              <TableCell>Event type</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sa.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell>{l.batchNo ?? "—"}</TableCell>
                <TableCell>{l.expiryDate ? new Date(l.expiryDate).toLocaleDateString() : "—"}</TableCell>
                <TableCell align="right" sx={{
                  fontWeight: 600,
                  color: l.qtyDelta < 0 ? "error.main" : "success.main",
                }}>
                  {l.qtyDelta > 0 ? "+" : ""}{l.qtyDelta}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={l.qtyDelta < 0 ? "error" : "success"}
                    label={l.qtyDelta < 0 ? "WRITE_OFF" : "ADJUSTMENT"}
                  />
                </TableCell>
                <TableCell>{l.lineReason ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="flex-end">
          <Stack>
            <Typography variant="caption" color="text.secondary">Total cost impact</Typography>
            <Typography variant="h6" color="primary">{sa.totalAmount}</Typography>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={1}>
        <Button onClick={() => navigate("/inventory/adjustments")}>Back to list</Button>
      </Stack>

      <Dialog open={postOpen} onClose={() => setPostOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Post stock adjustment {sa.documentNo}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Posting writes one immutable ledger row per line. Positive deltas post as
            <b> ADJUSTMENT</b>; negative deltas post as <b>WRITE_OFF</b>. This action is irreversible —
            corrections must be made via a new reversing adjustment.
          </DialogContentText>
          {postMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(postMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={postMutation.isPending}
            startIcon={postMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />}
            onClick={() => postMutation.mutate()}>
            Post
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
